import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { api, useAction } from '../../api/hooks';
import { useMe } from '../../api/session';
import type { AgentAuthorizationRequest } from '../../api/types';
import { Wordmark } from '../../layout/AppShell';
import { Button } from '../../ui/Button';
import { Alert, ErrorAlert, PageLoading } from '../../ui/feedback';
import { usePageTitle } from '../../ui/misc';

/** Consent for an agent's browser sign-in (the lixionary-jira plugin's login): the approved agent acts as the person's personal agent. */
export function ConnectAgentPage() {
  usePageTitle('Connect an AI agent');
  const me = useMe();
  const [params] = useSearchParams();
  const id = params.get('request') ?? '';
  const [leaving, setLeaving] = useState(false);
  const q = useQuery({
    queryKey: ['oauth-request', id],
    queryFn: () => api.get<AgentAuthorizationRequest>(`/oauth/requests/${encodeURIComponent(id)}`),
    enabled: !!id,
    retry: false,
  });
  const decide = useAction(
    (approve: boolean) =>
      api.post<{ redirectTo: string }>(
        `/oauth/requests/${encodeURIComponent(id)}/${approve ? 'approve' : 'deny'}`,
      ),
    {
      onSuccess: (r) => {
        setLeaving(true);
        window.location.assign(r.redirectTo);
      },
    },
  );

  if (id && q.isPending) return <PageLoading />;
  const r = q.data;
  return (
    <div className="auth-layout">
      <div className="auth-card wide stack">
        <Wordmark size={28} />
        {!id || q.error ? (
          <Alert tone="error" title="This sign-in link is not valid">
            Start the connection again from your agent.
          </Alert>
        ) : r && r.status !== 'pending' ? (
          <Alert tone="warning" title="This sign-in request has expired or was already answered">
            Start the connection again from your agent.
          </Alert>
        ) : leaving ? (
          <Alert tone="success" title="Returning to your agent">
            You can close this tab once your agent confirms the connection.
          </Alert>
        ) : (
          r && (
            <>
              <div className="stack sm">
                <h1 className="page-title">Connect {r.clientName}</h1>
                <p className="muted">
                  <strong className="ink">{r.clientName}</strong> wants to work in Lixionary Jira as{' '}
                  <strong className="ink">{r.agent.name}</strong>, your personal AI agent. You are signed in
                  as {me.user.displayName}.
                </p>
              </div>
              {!r.agent.active && (
                <Alert tone="error" title="Your agent is deactivated">
                  An admin has to reactivate it before you can connect.
                </Alert>
              )}
              <div className="stack sm">
                <span className="small strong">Projects</span>
                <span className="small muted">
                  {r.projects.all
                    ? 'All projects (you are an admin).'
                    : r.projects.items.length
                      ? r.projects.items.map((p) => p.key).join(', ')
                      : 'None yet. The agent gains access when you join a project.'}
                </span>
              </div>
              <div className="stack sm">
                <span className="small strong">It will be able to</span>
                <ul className="small muted" style={{ margin: 0, paddingLeft: 18 }}>
                  <li>Read tickets, discussion and project context</li>
                  {r.capabilities.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
                <span className="small muted">
                  It can never approve work, releases or rollbacks, or change members and roles. Those stay
                  with people.
                </span>
              </div>
              <span className="small muted">
                After you allow, the agent receives its sign-in at{' '}
                <span className="mono">{r.redirectTo}</span>. Only continue if you started this connection
                yourself. You can disconnect it at any time under My AI connections.
              </span>
              <ErrorAlert error={decide.error} />
              <div className="row" style={{ gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={() => decide.mutate(false)} disabled={decide.isPending}>
                  Deny
                </Button>
                <Button
                  onClick={() => decide.mutate(true)}
                  loading={decide.isPending}
                  disabled={!r.agent.active}
                >
                  Allow
                </Button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
